import { transformHexToString, transformStringToHex } from './hex';
import {
  ENCODED_URL_SEARCH_PARAM,
  getTrackingKeyFromDestinationUrl,
  PROTOCOL_REGEX,
} from './url';

export const EXTERNAL_BROWSER_REDIRECT_SEARCH_PARAM = 'ebr';
const REDIRECT_IGNORED_SEARCH_PARAMS = new Set(['fbclid']);

export const getEncodedExternalBrowserRedirectUrl = (url) => {
  if (typeof url !== 'string' || !url.trim()) {
    return null;
  }

  const trimmedUrl = url.trim();
  const absoluteUrl = trimmedUrl.startsWith('/')
    ? `${window.location.origin}${trimmedUrl}`
    : trimmedUrl;

  if (!PROTOCOL_REGEX.test(absoluteUrl)) {
    return null;
  }

  return transformStringToHex(absoluteUrl);
};

export const getExternalBrowserRedirectSearchValue = (search) => {
  const searchParams = new URLSearchParams(search);
  return searchParams.get(EXTERNAL_BROWSER_REDIRECT_SEARCH_PARAM);
};

export const buildSearchWithExternalBrowserRedirect = (search, encodedUrl) => {
  const sourceParams = new URLSearchParams(search);
  const normalizedParams = new URLSearchParams();

  // Keep redirect marker first to reduce the risks of truncation on handoff.
  normalizedParams.set(EXTERNAL_BROWSER_REDIRECT_SEARCH_PARAM, encodedUrl);

  sourceParams.forEach((value, key) => {
    if (
      key === EXTERNAL_BROWSER_REDIRECT_SEARCH_PARAM ||
      REDIRECT_IGNORED_SEARCH_PARAMS.has(key.toLowerCase())
    ) {
      return;
    }
    normalizedParams.append(key, value);
  });

  return normalizedParams.toString();
};

export const buildSearchWithoutExternalBrowserRedirect = (search) => {
  const searchParams = new URLSearchParams(search);
  searchParams.delete(EXTERNAL_BROWSER_REDIRECT_SEARCH_PARAM);
  return searchParams.toString();
};

export const hasExternalBrowserRedirectIntent = (
  url,
  search = typeof window !== 'undefined' ? window.location.search : ''
) => {
  const encodedRedirectUrl = getEncodedExternalBrowserRedirectUrl(url);
  if (!encodedRedirectUrl) {
    return false;
  }

  return getExternalBrowserRedirectSearchValue(search) === encodedRedirectUrl;
};

export const primeExternalBrowserRedirectIntent = (url) => {
  if (typeof window === 'undefined') {
    return false;
  }

  const encodedRedirectUrl = getEncodedExternalBrowserRedirectUrl(url);
  if (!encodedRedirectUrl) {
    return false;
  }

  const nextSearch = buildSearchWithExternalBrowserRedirect(
    window.location.search,
    encodedRedirectUrl
  );
  const currentSearch = window.location.search.replace(/^\?/, '');

  if (nextSearch === currentSearch) {
    return false;
  }

  window.location.assign(`${window.location.pathname}?${nextSearch}`);

  return true;
};

export const navigateToExternalBrowserRedirectIntent = (url) => {
  if (typeof window === 'undefined') {
    return false;
  }

  const encodedRedirectUrl = getEncodedExternalBrowserRedirectUrl(url);
  if (!encodedRedirectUrl) {
    return false;
  }

  const nextSearch = buildSearchWithExternalBrowserRedirect(
    window.location.search,
    encodedRedirectUrl
  );
  const nextUrl = `${window.location.pathname}?${nextSearch}${window.location.hash}`;
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (nextUrl === currentUrl) {
    return false;
  }

  window.location.replace(nextUrl);

  return true;
};

export const buildVisitUrlFromExternalBrowserRedirect = (encodedUrl) => {
  let decodedUrl = null;
  try {
    decodedUrl = transformHexToString(encodedUrl).trim();
  } catch (error) {
    decodedUrl = null;
  }

  const trackingParamKey = decodedUrl
    ? getTrackingKeyFromDestinationUrl(decodedUrl)
    : null;

  const base = `/visit?${ENCODED_URL_SEARCH_PARAM}=${encodeURIComponent(
    encodedUrl
  )}`;

  return trackingParamKey ? `${base}&${trackingParamKey}=1` : base;
};

export const getDecodedExternalBrowserRedirectUrl = (encodedUrl) => {
  if (!encodedUrl) {
    return null;
  }

  try {
    const decodedUrl = transformHexToString(encodedUrl).trim();
    if (!PROTOCOL_REGEX.test(decodedUrl)) {
      return null;
    }
    return decodedUrl;
  } catch (error) {
    return null;
  }
};

export const stripExternalBrowserRedirectFromUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.delete(EXTERNAL_BROWSER_REDIRECT_SEARCH_PARAM);
    return parsedUrl.toString();
  } catch (error) {
    return url;
  }
};
